import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Calendar, User, ChevronRight, BookOpen } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  category: string | null;
  author: string | null;
  published_at: string | null;
  created_at: string;
}

export default function Blog() {
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch published blog posts
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, featured_image, category, author, published_at, created_at")
        .eq("is_published", true)
        .order("published_at", { ascending: false, nullsFirst: false });
      
      if (error) throw error;
      return data as BlogPost[];
    },
  });

  // Get unique categories
  const categories = [...new Set(posts.map(p => p.category).filter(Boolean))];

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = !searchQuery || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (post.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <Helmet>
        <title>Blog – Musikpädagogik & Praxistipps | MusiKonzept</title>
        <meta name="description" content="Praxistipps, Fachwissen und Neuigkeiten rund um musikalische Früherziehung, Kita-Musik und pädagogische Fortbildung." />
        <link rel="canonical" href="https://www.musikonzept.de/blog" />
        <meta property="og:title" content="Blog – Musikpädagogik & Praxistipps | MusiKonzept" />
        <meta property="og:description" content="Praxistipps, Fachwissen und Neuigkeiten rund um musikalische Früherziehung, Kita-Musik und pädagogische Fortbildung." />
        <meta property="og:url" content="https://www.musikonzept.de/blog" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "MusiKonzept Blog",
          url: "https://www.musikonzept.de/blog",
          description: "Praxistipps, Fachwissen und Neuigkeiten rund um musikalische Früherziehung, Kita-Musik und pädagogische Fortbildung.",
          publisher: {
            "@type": "Organization",
            name: "MusiKonzept",
            logo: { "@type": "ImageObject", url: "https://www.musikonzept.de/musikonzept-logo.png" },
          },
        })}</script>
        <meta property="og:type" content="website" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-1">
          {/* Hero Section */}
          <section className="bg-gradient-to-b from-primary/5 to-background py-16">
            <div className="container mx-auto px-4 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h1 className="text-4xl font-bold">MusiKonzept Blog</h1>
              </div>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Praxistipps, Fachwissen und Neuigkeiten rund um musikalische Früherziehung
              </p>
              
              {/* Search */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  placeholder="Artikel durchsuchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 text-lg"
                />
              </div>
            </div>
          </section>

          {/* Category Filter */}
          {categories.length > 0 && (
            <section className="border-b">
              <div className="container mx-auto px-4 py-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={selectedCategory === null ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(null)}
                  >
                    Alle
                  </Button>
                  {categories.map((category) => (
                    <Button
                      key={category}
                      variant={selectedCategory === category ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCategory(category as string)}
                    >
                      {category}
                    </Button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Blog Posts Grid */}
          <section className="py-12">
            <div className="container mx-auto px-4">
              {isLoading ? (
                <div className="flex justify-center py-16">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-16">
                  <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h2 className="text-xl font-semibold mb-2">
                    {searchQuery ? "Keine Artikel gefunden" : "Noch keine Blogartikel"}
                  </h2>
                  <p className="text-muted-foreground">
                    {searchQuery 
                      ? "Versuche einen anderen Suchbegriff." 
                      : "Schau bald wieder vorbei für neue Inhalte!"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="block"
                    >
                      <Card
                        className="overflow-hidden cursor-pointer hover:shadow-lg transition-all group"
                      >
                        {post.featured_image && (
                          <div className="aspect-video overflow-hidden">
                            <img 
                              src={post.featured_image} 
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <CardContent className="p-5">
                          {post.category && (
                            <Badge variant="secondary" className="mb-3">
                              {post.category}
                            </Badge>
                          )}
                          <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                          {post.excerpt && (
                            <p className="text-muted-foreground line-clamp-3 mb-4">
                              {post.excerpt}
                            </p>
                          )}
                          <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <div className="flex items-center gap-4">
                              {post.author && (
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {post.author}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {format(new Date(post.published_at || post.created_at), "d. MMM yyyy", { locale: de })}
                              </span>
                            </div>
                            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
